/**
 * GraphCanvas Component - Cytoscape.js Visualization
 * Baseado em best practices 2025: useRef pattern, container-based initialization
 *
 * Padrão:
 * - useRef para armazenar instância Cytoscape (não causa re-render)
 * - useGraph hook customizado para lifecycle e events
 * - Responsivo (100% width/height do container)
 *
 * Referências:
 * - https://github.com/plotly/react-cytoscapejs/issues/51
 * - https://bilalshafqat.com/react-useref-hook-explained-with-real-examples/
 */

import { useEffect } from 'react';
import { NodeSingular } from 'cytoscape';
import { useGraph } from '../../hooks/useGraph';
import type { Investigation } from '../../types/investigation';
import { Spinner } from '../ui/Spinner';

interface GraphCanvasProps {
  investigation: Investigation | null;
  onNodeClick?: (node: NodeSingular) => void;
  onNodeExpand?: (nodeId: string) => void;
  onBackgroundClick?: () => void;
  className?: string;
}

/**
 * Canvas principal do grafo usando Cytoscape.js
 *
 * Features:
 * - ✅ Integração com useGraph hook
 * - ✅ Auto-load quando investigation muda
 * - ✅ Click handlers customizáveis
 * - ✅ Loading state
 * - ✅ Responsivo e touch-friendly
 *
 * @example
 * <GraphCanvas
 *   investigation={investigation}
 *   onNodeClick={(node) => setSelectedNode(node)}
 *   onNodeExpand={(id) => expandNode(investigationId, id)}
 * />
 */
export function GraphCanvas({
  investigation,
  onNodeClick,
  onNodeExpand,
  onBackgroundClick,
  className = '',
}: GraphCanvasProps) {

  // Hook customizado para gerenciar Cytoscape
  const { containerRef, loading, loadInvestigation, selectedNode } = useGraph({
    onNodeClick: (node) => {
      onNodeClick?.(node);
    },
    onNodeTap: (node) => {
      // Mobile tap - se nó não expandido, expandir
      const isExpanded = node.data('expandido');
      if (!isExpanded && onNodeExpand) {
        onNodeExpand(node.id());
      }
    },
    onBackgroundClick,
  });

  // Carregar investigação quando mudar
  useEffect(() => {
    if (investigation) {
      loadInvestigation(investigation);
    }
  }, [investigation, loadInvestigation]);

  return (
    <div className={`relative w-full h-full ${className}`}>
      {/* Container Cytoscape - ref callback inicializa o grafo */}
      <div
        ref={containerRef}
        className="w-full h-full bg-gray-50"
        style={{ minHeight: '400px' }}
      />

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
          <div className="text-center">
            <Spinner size="xl" color="blue" />
            <p className="mt-4 text-sm text-gray-600">Carregando grafo...</p>
          </div>
        </div>
      )}

      {/* Empty state - se não houver investigação */}
      {!investigation && !loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center max-w-sm px-4">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma investigação selecionada</h3>
            <p className="mt-1 text-sm text-gray-500">
              Selecione uma investigação existente ou crie uma nova para visualizar o grafo.
            </p>
          </div>
        </div>
      )}

      {/* Info overlay - Nó selecionado (mobile) */}
      {selectedNode && (
        <div className="absolute bottom-4 left-4 right-4 md:hidden bg-white rounded-lg shadow-lg p-3 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Nó selecionado</p>
              <p className="text-sm font-medium text-gray-900 truncate">
                {selectedNode.data('label')}
              </p>
              <p className="text-xs text-gray-600">
                Tipo: <span className="font-medium">{selectedNode.data('type')}</span>
              </p>
            </div>
            {!selectedNode.data('expandido') && (
              <button
                onClick={() => onNodeExpand?.(selectedNode.id())}
                className="ml-2 px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors min-h-[32px]"
              >
                Expandir
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
